<template>
    <div class="main-content">
        <!-- TOASTS DE ALERTA -->
        <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1060">
            <transition-group name="slide-fade">
                <div v-for="alert in alerts" :key="alert.id"
                    class="toast show align-items-center border-0 shadow-lg mb-2"
                    :class="'bg-' + alert.type + '-subtle text-' + alert.type" role="alert">
                    <div class="d-flex p-3">
                        <i :class="alert.icon" class="me-3 fs-5"></i>
                        <div class="toast-body p-0 fw-medium">
                            {{ alert.message }}
                        </div>
                        <button type="button" class="btn-close ms-auto" @click="removeAlert(alert.id)"></button>
                    </div>
                </div>
            </transition-group>
        </div>

        <!-- BREADCRUMB -->
        <header class="content-header mb-2 mt-n5">
            <nav aria-label="breadcrumb">
                <ol class="breadcrumb mb-0">
                    <li class="breadcrumb-item"> <router-link class="nav-link active" to="/">Início</router-link></li>
                    <li class="breadcrumb-item active text-dark small">Doações</li>
                </ol>
            </nav>
        </header>

        <!-- CONTEÚDO PRINCIPAL -->
        <div class="card p-4 shadow-subtle rounded-4 border-0">
            <!-- CABEÇALHO DA TABELA -->
            <div class="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom flex-wrap gap-3">
                <div>
                    <h4 class="mb-0 fw-bold">Doações Recebidas</h4>
                    <span class="text-muted fs-7">Exibindo {{ donations.length }} de {{ totalItems }}</span>
                </div>

                <div class="d-flex gap-2 align-items-center">
                    <!-- Botão de Filtro -->
                    <button class="btn btn-pmg-light fw-medium" @click="showFilter = !showFilter"
                        :class="{ 'active': showFilter }">
                        <i class="bi bi-funnel me-1"></i> Filtrar Buscas
                    </button>

                    <!-- Exportar (Sugestão para dados financeiros) -->
                    <button class="btn btn-pmg-primary">
                        <i class="bi bi-download me-1"></i> Exportar
                    </button>
                </div>
            </div>

            <!-- FILTROS (COLLAPSE) -->
            <div class="collapse" :class="{ 'show': showFilter }">
                <div class="card card-body mb-4 border-0 bg-light rounded-3">
                    <div class="row g-3">
                        <div class="col-md-4">
                            <label class="form-label small fw-bold text-muted mb-1">Busca Geral</label>
                            <input v-model="filter.search" type="text" class="form-control form-control-pmg"
                                placeholder="Nome, e-mail ou documento...">
                        </div>
                        <div class="col-md-2">
                            <label class="form-label small fw-bold text-muted mb-1">Status</label>
                            <select v-model="filter.status" class="form-select form-control-pmg">
                                <option value="">Todos</option>
                                <option value="paid">Pagos</option>
                                <option value="pending">Pendentes</option>
                                <option value="canceled">Cancelados</option>
                            </select>
                        </div>
                        <div class="col-md-2">
                            <label class="form-label small fw-bold text-muted mb-1">Data Inicial</label>
                            <input v-model="filter.created_start_date" type="date"
                                class="form-control form-control-pmg">
                        </div>
                        <div class="col-md-2">
                            <label class="form-label small fw-bold text-muted mb-1">Data Final</label>
                            <input v-model="filter.created_end_date" type="date" class="form-control form-control-pmg">
                        </div>
                        <div class="col-md-2 d-flex align-items-end">
                            <button class="btn btn-dark w-100 btn-pmg-dark fw-bold py-2" @click="applyFilter">
                                <i class="bi bi-search me-1"></i> Filtrar
                            </button>
                        </div>
                    </div>
                    <!-- Linha 2 de filtros (Valores) -->
                    <div class="row g-3 mt-1">
                        <div class="col-md-2">
                            <label class="form-label small fw-bold text-muted mb-1">Valor Mínimo</label>
                            <input type="text" class="form-control form-control-pmg" placeholder="R$ 0,00"
                                :value="displayFilter.min_amount" @input="handleMoneyInput($event, 'min_amount')">
                        </div>

                        <div class="col-md-2">
                            <label class="form-label small fw-bold text-muted mb-1">Valor Máximo</label>
                            <input type="text" class="form-control form-control-pmg" placeholder="R$ 0,00"
                                :value="displayFilter.max_amount" @input="handleMoneyInput($event, 'max_amount')">
                        </div>
                        <div class="col-md-8 d-flex align-items-end justify-content-end">
                            <button class="btn btn-link text-muted text-decoration-none small" @click="clearFilters"
                                v-if="hasActiveFilters">
                                Limpar filtros
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- TABELA DE DOAÇÕES -->
            <div class="table-responsive">
                <table class="table table-pmg align-middle">
                    <thead>
                        <tr>
                            <th style="width: 22%">Doador</th>
                            <th style="width: 18%">Campanha (LP)</th>
                            <th style="width: 25%">Resumo Financeiro (Bruto / Taxas / Líquido)</th>
                            <th style="width: 12%">Status</th>
                            <th style="width: 13%">Data</th>
                            <th style="width: 10%" class="text-end">Ações</th>
                        </tr>
                    </thead>

                    <tbody v-if="donations.length > 0">
                        <tr v-for="item in donations" :key="item.id">
                            <td>
                                <div class="fw-bold text-dark mb-1">{{ item.name_client || 'Anônimo' }}</div>
                                <div class="text-muted small text-truncate-custom" style="max-width: 200px;">
                                    {{ item.email_client }}
                                </div>
                            </td>
                            <td>
                                <div class="pmg-desc-cell fw-medium">
                                    {{ item.landing_page?.title || 'Campanha Removida' }}
                                </div>
                            </td>
                            <td>
                                <!-- Linha 1: Líquido em Destaque + Badge do Método -->
                                <div class="d-flex align-items-center gap-2 mb-1">
                                    <span class="fw-bold text-success">
                                        R$ {{ formatCurrency(item.amount - (item.total_fees_amount || 0)) }}
                                    </span>
                                    <span
                                        class="badge bg-light text-secondary border border-light-subtle rounded-pill py-1 px-2"
                                        style="font-size: 0.7rem;">
                                        <i :class="getPaymentIcon(item.payment_method)"></i> {{
                                            formatPaymentMethod(item.payment_method) }}
                                    </span>
                                </div>

                                <!-- Linha 2: Bruto + Taxa interativa com Tooltip -->
                                <div class="text-muted" style="font-size: 0.75rem;">
                                    Bruto: R$ {{ formatCurrency(item.amount) }}
                                    <span class="text-danger ms-1 fw-medium position-relative pmg-tooltip-wrapper"
                                        style="cursor: help;">
                                        • Taxa: -R$ {{ formatCurrency(item.total_fees_amount || 0) }}
                                        <i class="bi bi-info-circle ms-1" style="font-size: 0.7rem;"></i>

                                        <!-- CARD DO TOOLTIP (Aparece no Hover) -->
                                        <div class="pmg-tooltip-box shadow-sm rounded-3 p-2">
                                            <div class="fw-bold mb-1 border-bottom pb-1 text-dark">
                                                Taxa {{ formatPaymentMethod(item.payment_method) }}
                                            </div>
                                            <div class="text-secondary small">
                                                • Taxa Fixa: <strong>R$ {{ formatCurrency(getBreakdownField(item,
                                                    'fixed_fee')) }}</strong>
                                            </div>
                                            <div class="text-secondary small">
                                                • Percentual ({{ getBreakdownField(item, 'percentage_rate') }}%):
                                                <strong>R$ {{ formatCurrency(getBreakdownField(item,
                                                    'percentage_amount')) }}</strong>
                                            </div>
                                            <div class="border-top mt-1 pt-1 fw-bold text-danger small">
                                                Total: -R$ {{ formatCurrency(item.total_fees_amount || 0) }}
                                            </div>
                                        </div>
                                    </span>
                                </div>
                            </td>
                            <td>
                                <span class="badge rounded-pill px-3 py-2" :class="getStatusClass(item.status)">
                                    {{ getStatusText(item.status) }}
                                </span>
                            </td>
                            <td>
                                <div class="text-secondary small">
                                    <i class="bi bi-calendar3 me-1"></i> {{ formatDate(item.created_at) }}
                                </div>
                            </td>
                            <td class="text-end">
                                <div class="dropdown">
                                    <button class="btn btn-pmg-icon border" data-bs-toggle="dropdown"
                                        data-bs-boundary="viewport">
                                        <i class="bi bi-three-dots-vertical"></i>
                                    </button>
                                    <ul class="dropdown-menu dropdown-menu-end shadow border-0 p-2 rounded-3">
                                        <li>
                                            <a class="dropdown-item rounded-2 py-2 d-flex align-items-center" href="#"
                                                @click.prevent="openDetailsModal(item)">
                                                <i class="bi bi-eye text-primary me-2"></i> Ver Detalhes
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </td>
                        </tr>
                    </tbody>

                    <!-- ESTADO VAZIO -->
                    <tbody v-else>
                        <tr>
                            <td colspan="6" class="text-center py-5 text-muted">
                                <div class="d-flex flex-column align-items-center justify-content-center">
                                    <i class="bi bi-inbox fs-1 mb-2 text-secondary opacity-50"></i>
                                    <span class="fw-medium">Nenhuma doação encontrada.</span>
                                    <span class="small mt-1" v-if="hasActiveFilters">Tente limpar os filtros de
                                        busca.</span>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- BOTÃO CARREGAR MAIS -->
            <div class="text-center mt-4" v-if="hasMore">
                <button
                    class="btn btn-pmg-light px-5 btn-sm fw-bold d-inline-flex align-items-center justify-content-center"
                    @click="loadMore" :disabled="loading" style="min-width: 220px;">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                    <span>{{ loading ? 'Carregando dados...' : 'Carregar mais doações' }}</span>
                </button>
            </div>
        </div>

        <!-- MODAL DE DETALHES DA DOAÇÃO -->
        <div class="modal fade" id="modalDonationDetails" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content border-0 shadow-lg rounded-4">
                    <div class="modal-header border-0 pt-4 px-4 pb-2">
                        <h5 class="fw-bold m-0 d-flex align-items-center">
                            <i class="bi bi-receipt me-2 text-primary"></i> Detalhes da Doação
                        </h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body px-4 pb-4" v-if="selectedDonation">

                        <div
                            class="card bg-light border-0 p-3 mb-4 rounded-3 d-flex flex-row justify-content-between align-items-center">
                            <div>
                                <small class="text-muted d-block fw-bold mb-1">Valor da Doação</small>
                                <h3 class="text-dark fw-bold mb-0">R$ {{ formatCurrency(selectedDonation.amount) }}</h3>
                            </div>
                            <div class="text-end">
                                <span class="badge rounded-pill px-3 py-2 mb-1"
                                    :class="getStatusClass(selectedDonation.status)">
                                    {{ getStatusText(selectedDonation.status) }}
                                </span>
                            </div>
                        </div>

                        <div class="row g-3 mb-3">
                            <div class="col-12">
                                <label class="small fw-bold text-muted mb-1">Doador</label>
                                <div class="fw-medium text-dark">{{ selectedDonation.name_client || 'Não informado' }}
                                </div>
                                <div class="small text-secondary">{{ selectedDonation.email_client }}</div>
                            </div>
                            <div class="col-6">
                                <label class="small fw-bold text-muted mb-1">Documento</label>
                                <div class="fw-medium text-dark">{{ selectedDonation.document_client || 'N/A' }}</div>
                            </div>
                            <div class="col-6">
                                <label class="small fw-bold text-muted mb-1">Telefone</label>
                                <div class="fw-medium text-dark">{{ selectedDonation.phone_client || 'N/A' }}</div>
                            </div>
                            <div class="col-6">
                                <label class="small fw-bold text-muted mb-1">Método de Pagamento</label>
                                <div class="fw-medium text-dark">{{ formatPaymentMethod(selectedDonation.payment_method)
                                }}</div>
                            </div>
                            <div class="col-6">
                                <label class="small fw-bold text-muted mb-1">Data da Transação</label>
                                <div class="fw-medium text-dark">{{ formatDate(selectedDonation.created_at) }}</div>
                            </div>
                        </div>

                        <!-- Breakdown de Taxas (Opcional visualização para o gestor) -->
                        <div class="border-top pt-3 mt-2" v-if="selectedDonation.total_fees_amount">
                            <label class="small fw-bold text-muted mb-2 d-block">Resumo Financeiro (Auditoria)</label>
                            <div class="d-flex justify-content-between small mb-1">
                                <span class="text-secondary">Desconto de Taxas</span>
                                <span class="text-danger fw-medium">- R$ {{
                                    formatCurrency(selectedDonation.total_fees_amount) }}</span>
                            </div>
                            <div class="d-flex justify-content-between small fw-bold">
                                <span class="text-dark">Valor Líquido Estimado</span>
                                <span class="text-success">R$ {{ formatCurrency(selectedDonation.amount -
                                    selectedDonation.total_fees_amount) }}</span>
                            </div>
                        </div>

                    </div>
                    <div class="modal-footer border-0 pb-4 px-4">
                        <button class="btn btn-pmg-light w-100 fw-bold" data-bs-dismiss="modal">Fechar</button>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import api from '@/services/api';

export default {
    data() {
        return {
            donations: [],
            alerts: [],
            filter: {
                search: '',
                status: '',
                created_start_date: '',
                created_end_date: '',
                min_amount: '',
                max_amount: ''
            },
            displayFilter: {
                min_amount: '',
                max_amount: ''
            },

            // Paginação
            totalItems: 0,
            currentPage: 1,
            lastPage: 1,
            loading: false,
            hasMore: false,

            // Interface
            showFilter: false,
            modalInstance: null,
            selectedDonation: null,
        }
    },
    computed: {
        hasActiveFilters() {
            return Object.values(this.filter).some(val => val !== '' && val !== null);
        }
    },
    mounted() {
        // Inicializa o modal de detalhes
        const modalElement = document.getElementById('modalDonationDetails');
        if (modalElement) {
            this.modalInstance = new window.bootstrap.Modal(modalElement);
        }

        // Busca a listagem inicial
        this.fetchDonations();
    },
    methods: {
        async fetchDonations(page = 1, append = false) {
            this.loading = true;
            try {
                const params = {
                    page: page,
                    ...this.filter // Envia todos os filtros preenchidos
                };

                const response = await api.get('/client-jwt/landing-pages/donations', { params });

                // Trata a estrutura de resposta (Padrão JSON Resource Wrapper)
                const payload = response.data.data;
                const items = payload.data || [];

                if (append) {
                    this.donations = [...this.donations, ...items];
                } else {
                    this.donations = items;
                }

                this.currentPage = payload.current_page;
                this.lastPage = payload.last_page;
                this.totalItems = payload.total;
                this.hasMore = this.currentPage < this.lastPage;

            } catch (error) {
                this.showAlert('Falha ao comunicar com o servidor. Verifique os filtros.', 'error');
                console.error("Erro na API de Doações:", error);
            } finally {
                this.loading = false;
            }
        },

        loadMore() {
            if (this.hasMore && !this.loading) {
                this.fetchDonations(this.currentPage + 1, true);
            }
        },

        applyFilter() {
            this.showFilter = false;
            this.fetchDonations(1, false);
        },

        clearFilters() {
            this.filter = {
                search: '',
                status: '',
                created_start_date: '',
                created_end_date: '',
                min_amount: '',
                max_amount: ''
            };
            this.displayFilter = {
                min_amount: '',
                max_amount: ''
            };
            this.fetchDonations(1, false);
        },

        openDetailsModal(item) {
            this.selectedDonation = item;
            if (this.modalInstance) {
                this.modalInstance.show();
            }
        },

        // --- MÉTODOS AUXILIARES DE FORMATAÇÃO ---

        formatCurrency(value) {
            if (!value) return '0,00';
            return parseFloat(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },

        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        },

        formatPaymentMethod(method) {
            const methods = {
                'pix': 'PIX',
                'credit_card': 'Cartão de Crédito',
                'boleto': 'Boleto Bancário'
            };
            return methods[method] || method;
        },

        getPaymentIcon(method) {
            const icons = {
                'pix': 'bi-qr-code text-success',
                'credit_card': 'bi-credit-card text-primary',
                'boleto': 'bi-upc-scan text-dark'
            };
            return icons[method] || 'bi-cash';
        },

        getStatusText(status) {
            const statuses = {
                'paid': 'Pago',
                'pending': 'Pendente',
                'canceled': 'Cancelado',
                'refunded': 'Reembolsado'
            };
            return statuses[status] || status;
        },

        getStatusClass(status) {
            const classes = {
                'paid': 'bg-success-subtle text-success border border-success-subtle',
                'pending': 'bg-warning-subtle text-warning border border-warning-subtle',
                'canceled': 'bg-danger-subtle text-danger border border-danger-subtle',
                'refunded': 'bg-secondary-subtle text-secondary border border-secondary-subtle'
            };
            return classes[status] || 'bg-light text-dark';
        },

        // --- SISTEMA DE ALERTAS (TOASTS) ---
        showAlert(message, type = 'success') {
            const id = Date.now();
            let icon = 'bi-check-circle-fill';
            if (type === 'danger' || type === 'error') icon = 'bi-exclamation-circle-fill';
            if (type === 'warning') icon = 'bi-exclamation-triangle-fill';

            this.alerts.push({ id, message, type: type === 'error' ? 'danger' : type, icon });
            setTimeout(() => this.removeAlert(id), 5000);
        },
        removeAlert(id) {
            this.alerts = this.alerts.filter(a => a.id !== id);
        },
        handleMoneyInput(event, field) {
            // 1. Remove tudo que não for dígito
            let digits = event.target.value.replace(/\D/g, '');

            if (!digits) {
                this.filter[field] = '';
                this.displayFilter[field] = '';
                event.target.value = '';
                return;
            }

            // 2. Converte para o valor real em float (divisao por 100 para pegar os centavos)
            const floatValue = parseFloat(digits) / 100;

            // 3. Salva no filter o número limpo que será enviado para a API (ex: 150.25)
            this.filter[field] = floatValue;

            // 4. Formata a máscara de exibição visual (ex: "R$ 150,25")
            const formatted = floatValue.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            });

            // 5. Atualiza o input e o estado de exibição
            this.displayFilter[field] = formatted;
            event.target.value = formatted;
        },

       getBreakdownField(item, field) {
        if (!item || !item.fees_breakdown) return 0;

        let breakdown = item.fees_breakdown;

        // Se a API retornou como string JSON, faz o parse
        if (typeof breakdown === 'string') {
            try {
                breakdown = JSON.parse(breakdown);
            } catch (e) {
                return 0;
            }
        }

        return breakdown[field] || 0;
    },


    }
}
</script>

<style scoped>
/* Mantemos estritamente o mesmo CSS global do componente de Categorias para padrão visual */
.toast {
    min-width: 300px;
    border-radius: 12px;
    background-color: white !important;
}

.main-content {
    background-color: #f8fafc;
    min-height: 100vh;
    padding: 2rem;
    font-family: 'Inter', sans-serif;
}

.card {
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.02);
}

.btn-pmg-primary {
    background: #561569;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 10px 20px;
    font-weight: 500;
}

.btn-pmg-light {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    color: #64748b;
    padding: 10px 20px;
}

.form-control-pmg {
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    padding: 12px;
}

.form-control-pmg:focus {
    border-color: #561569;
    box-shadow: 0 0 0 4px rgba(33, 51, 97, 0.1);
}

.table-pmg thead th {
    background: #f8fafc;
    color: #64748b;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 1.2rem;
    border-bottom: 1px solid #edf2f7;
}

.table-pmg tbody td {
    padding: 1.2rem;
    border-bottom: 1px solid #f1f5f9;
}

.pmg-desc-cell {
    color: #64748b;
    font-size: 0.9rem;
}

.text-truncate-custom {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.btn-pmg-icon.border {
    border-color: #e2e8f0 !important;
    background: white;
    width: 38px;
    height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
}

.btn-pmg-icon:hover {
    background-color: #f1f5f9;
}

/* Cores Adicionais para Status de Doação */
.bg-success-subtle {
    background-color: #ecfdf5 !important;
    color: #059669 !important;
}

.bg-warning-subtle {
    background-color: #fffbeb !important;
    color: #d97706 !important;
}

.bg-danger-subtle {
    background-color: #fef2f2 !important;
    color: #dc2626 !important;
}

.bg-secondary-subtle {
    background-color: #f1f5f9 !important;
    color: #475569 !important;
}

/* Animações */
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.4s ease;
}

.slide-fade-enter-from {
    transform: translateX(100px);
    opacity: 0;
}

.slide-fade-leave-to {
    opacity: 0;
    transform: scale(0.9);
}

/* CONTAINER E ANIMAÇÃO DO TOOLTIP INSTANTÂNEO */
.pmg-tooltip-wrapper {
    display: inline-block;
}

.pmg-tooltip-box {
    display: none;
    position: absolute;
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
    background: #ffffff;
    border: 1px solid #e2e8f0;
    width: 210px;
    z-index: 100;
    pointer-events: none;
    text-align: left;
    white-space: normal;
}

/* Exibe ao passar o mouse */
.pmg-tooltip-wrapper:hover .pmg-tooltip-box {
    display: block;
    animation: fadeIn 0.2s ease-in-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translate(-50%, 5px);
    }

    to {
        opacity: 1;
        transform: translate(-50%, 0);
    }
}
</style>